import type { ReactFlowInstance } from '@xyflow/react';
import { create } from 'zustand';
import type { Step } from '../lib/explanationRefs';
import type { TourFocus } from '../lib/tour';

export type InteractionMode = 'select' | 'pan';

interface DiagramViewState {
  instance: ReactFlowInstance | null;
  setInstance: (instance: ReactFlowInstance | null) => void;
  interactionMode: InteractionMode;
  setInteractionMode: (mode: InteractionMode) => void;
  /** Ids of the canvas nodes picked by a marquee / multi-select (only populated when more than one is selected). */
  multiSelectedIds: string[];
  setMultiSelectedIds: (ids: string[]) => void;
  /** Whether the dot grid is drawn behind the canvas (remembered across sessions). */
  showGrid: boolean;
  toggleGrid: () => void;
  /** True when the signed-in user may view but not change the open diagram. */
  readOnly: boolean;
  setReadOnly: (readOnly: boolean) => void;
  /** Set when something (the toolbar's About button) asks the side panel to show the explanation tab. */
  docsRequested: boolean;
  requestDocs: (requested: boolean) => void;
  /** Reference links in the explanation select things in the diagram; this keeps the About tab open while they do. */
  keepAboutOpen: boolean;
  /** A field (column) the explanation pointed at, highlighted on its table for a few seconds. */
  focusedColumn: { tableId: string; columnId: string } | null;
  focusColumn: (tableId: string, columnId: string) => void;
  /** Wider side panel for comfortable reading of long explanations. */
  panelWide: boolean;
  togglePanelWide: () => void;
  /** Asked for by the canvas context menu: open the About tab and scroll to where these steps point in the explanation. */
  explanationRequest: { steps: Step[] } | null;
  requestExplanation: (steps: Step[]) => void;
  clearExplanationRequest: () => void;
  /** The guided "story" tour of an architecture diagram: whether it is open, where it starts, and what it is lighting up. */
  tourOpen: boolean;
  tourStart: number;
  openTour: (startIndex?: number) => void;
  closeTour: () => void;
  tourFocus: TourFocus | null;
  setTourFocus: (focus: TourFocus | null) => void;
  toast: string | null;
  showToast: (message: string) => void;
  /** While true, the canvas renders every node/edge regardless of viewport culling.
   * Needed so a full-diagram export can capture nodes that are currently scrolled out
   * of view — onlyRenderVisibleElements unmounts those from the DOM entirely, so no
   * amount of transform math on the export clone can make them appear otherwise. */
  forceRenderAll: boolean;
  setForceRenderAll: (value: boolean) => void;
}

const GRID_KEY = 'hyphen-db-studio:show-grid';

function loadShowGrid(): boolean {
  try {
    return localStorage.getItem(GRID_KEY) !== 'false';
  } catch {
    return true;
  }
}

let focusTimer: ReturnType<typeof setTimeout> | undefined;
let toastTimer: ReturnType<typeof setTimeout> | undefined;

/** Ephemeral (non-persisted) handle to the live React Flow instance, so keyboard shortcuts
 * defined outside the canvas (fit view, etc.) can drive it without prop-drilling. */
export const useDiagramViewStore = create<DiagramViewState>((set) => ({
  instance: null,
  setInstance: (instance) => set({ instance }),
  interactionMode: 'select',
  setInteractionMode: (mode) => set({ interactionMode: mode }),
  multiSelectedIds: [],
  setMultiSelectedIds: (ids) => set((s) => (s.multiSelectedIds.length === 0 && ids.length === 0 ? s : { multiSelectedIds: ids })),
  showGrid: loadShowGrid(),
  toggleGrid: () =>
    set((s) => {
      const showGrid = !s.showGrid;
      try {
        localStorage.setItem(GRID_KEY, String(showGrid));
      } catch {
        // ignore
      }
      return { showGrid };
    }),
  readOnly: false,
  setReadOnly: (readOnly) => set({ readOnly }),
  docsRequested: false,
  requestDocs: (requested) => set({ docsRequested: requested }),
  keepAboutOpen: false,
  focusedColumn: null,
  focusColumn: (tableId, columnId) => {
    if (focusTimer) clearTimeout(focusTimer);
    set({ focusedColumn: { tableId, columnId } });
    focusTimer = setTimeout(() => set({ focusedColumn: null }), 4000);
  },
  panelWide: false,
  togglePanelWide: () => set((s) => ({ panelWide: !s.panelWide })),
  explanationRequest: null,
  requestExplanation: (steps) => set({ explanationRequest: { steps }, docsRequested: true }),
  clearExplanationRequest: () => set({ explanationRequest: null }),
  tourOpen: false,
  tourStart: 0,
  openTour: (startIndex = 0) => set({ tourOpen: true, tourStart: startIndex }),
  closeTour: () => set({ tourOpen: false, tourFocus: null }),
  tourFocus: null,
  setTourFocus: (focus) => set({ tourFocus: focus }),
  toast: null,
  showToast: (message) => {
    if (toastTimer) clearTimeout(toastTimer);
    set({ toast: message });
    toastTimer = setTimeout(() => set({ toast: null }), 2200);
  },
  forceRenderAll: false,
  setForceRenderAll: (value) => set({ forceRenderAll: value }),
}));
